import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, TrendingUp, AlertCircle } from 'lucide-react';
interface BenchmarksViewProps {
  activeRegion?: string;
}
interface ModelPerformance {
  name: string;
  color: string;
  mae: number;
  rmse: number;
  bias: number;
  score: number;
  status: 'Active' | 'Inactive';
}
const models: ModelPerformance[] = [{
  name: 'Jua EPT-2',
  color: '#ff006b',
  mae: 1.24,
  rmse: 1.82,
  bias: 0.12,
  score: 98.5,
  status: 'Active'
}, {
  name: 'ECMWF IFS',
  color: '#06b6d4',
  mae: 1.45,
  rmse: 2.1,
  bias: -0.34,
  score: 92.1,
  status: 'Active'
}, {
  name: 'GFS',
  color: '#f59e0b',
  mae: 1.89,
  rmse: 2.65,
  bias: 0.56,
  score: 85.4,
  status: 'Active'
}, {
  name: 'ICON-EU',
  color: '#8b5cf6',
  mae: 1.67,
  rmse: 2.34,
  bias: -0.21,
  score: 88.9,
  status: 'Active'
}];
export function BenchmarksView({
  activeRegion = 'Germany'
}: BenchmarksViewProps) {
  const topModel = models[0];
  const improvementRate = 2.4;
  const extremeEventsRate = 99.8;
  return <div className="flex flex-col h-full gap-6" data-id="element-863">
      {/* Header */}
      <div data-id="element-864">
        <h1 className="text-2xl font-bold text-slate-900 mb-1" data-id="element-865">
          Model Benchmarks
        </h1>
        <p className="text-sm text-slate-500" data-id="element-866">
          Performance comparison across major weather models (Last 30 Days)
        </p>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-867">
        {/* Top Performer - Highlighted */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="relative bg-gradient-to-br from-pink-50 via-white to-purple-50 backdrop-blur-sm border border-pink-200/60 rounded-2xl p-6 shadow-lg shadow-pink-500/10 ring-1 ring-pink-500/10" data-id="element-868">
          {/* Subtle glow effect */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-pink-500/5 to-purple-500/5 pointer-events-none" data-id="element-869" />

          <div className="relative" data-id="element-870">
            <div className="flex items-start gap-4 mb-4" data-id="element-871">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-pink-500/30" data-id="element-872">
                <Trophy size={24} className="text-white" data-id="element-873" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-874">
                <div className="text-sm font-medium text-pink-600 mb-2" data-id="element-875">
                  Top Performer
                </div>
                <div className="text-2xl font-bold text-slate-900" data-id="element-876">
                  {topModel.name}
                </div>
              </div>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed" data-id="element-877">
              Outperforming global models by{' '}
              <span className="font-semibold text-green-600" data-id="element-878">14.2%</span> in
              wind speed accuracy.
            </p>
          </div>
        </motion.div>

        {/* Improvement Rate */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.1
      }} className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-6 shadow-sm" data-id="element-879">
          <div className="flex items-start gap-4 mb-4" data-id="element-880">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-881">
              <TrendingUp size={24} className="text-blue-600" data-id="element-882" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-883">
              <div className="text-sm font-medium text-slate-500 mb-2" data-id="element-884">
                Improvement Rate
              </div>
              <div className="text-2xl font-bold text-slate-900" data-id="element-885">
                +{improvementRate}% / mo
              </div>
            </div>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed" data-id="element-886">
            Consistent accuracy improvements through continuous training.
          </p>
        </motion.div>

        {/* Extreme Events */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.2
      }} className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-6 shadow-sm" data-id="element-887">
          <div className="flex items-start gap-4 mb-4" data-id="element-888">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center flex-shrink-0" data-id="element-889">
              <AlertCircle size={24} className="text-amber-600" data-id="element-890" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-891">
              <div className="text-sm font-medium text-slate-500 mb-2" data-id="element-892">
                Extreme Events
              </div>
              <div className="text-2xl font-bold text-slate-900" data-id="element-893">
                {extremeEventsRate}%
              </div>
            </div>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed" data-id="element-894">
            Capture rate for high-impact weather events.
          </p>
        </motion.div>
      </div>

      {/* Performance Matrix */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      delay: 0.3
    }} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-6 flex-1 overflow-hidden flex flex-col" data-id="element-895">
        <h2 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-896">
          Performance Matrix
        </h2>

        <div className="flex-1 overflow-x-auto" data-id="element-897">
          <table className="w-full" data-id="element-898">
            <thead data-id="element-899">
              <tr className="border-b border-slate-200" data-id="element-900">
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider pb-3 pr-4" data-id="element-901">
                  Model Name
                </th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider pb-3 px-4" data-id="element-902">
                  MAE (M/S)
                </th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider pb-3 px-4" data-id="element-903">
                  RMSE
                </th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider pb-3 px-4" data-id="element-904">
                  BIAS
                </th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider pb-3 px-4" data-id="element-905">
                  Score
                </th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider pb-3 pl-4" data-id="element-906">
                  Status
                </th>
              </tr>
            </thead>
            <tbody data-id="element-907">
              {models.map((model, index) => <motion.tr key={model.name} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.3,
              delay: 0.4 + index * 0.1
            }} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors" data-id="element-908">
                  <td className="py-4 pr-4" data-id="element-909">
                    <div className="flex items-center gap-2" data-id="element-910">
                      <div className="w-2 h-2 rounded-full" style={{
                    backgroundColor: model.color
                  }} data-id="element-911" />
                      <span className="text-sm font-medium text-slate-900" data-id="element-912">
                        {model.name}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4" data-id="element-913">
                    <span className="text-sm text-slate-700 font-mono" data-id="element-914">
                      {model.mae.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-4 px-4" data-id="element-915">
                    <span className="text-sm text-slate-700 font-mono" data-id="element-916">
                      {model.rmse.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-4 px-4" data-id="element-917">
                    <span className={`text-sm font-mono font-medium ${model.bias > 0 ? 'text-red-600' : 'text-blue-600'}`} data-id="element-918">
                      {model.bias > 0 ? '+' : ''}
                      {model.bias.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-4 px-4" data-id="element-919">
                    <div className="flex items-center gap-2" data-id="element-920">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden max-w-[100px]" data-id="element-921">
                        <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: `${model.score}%`
                    }} transition={{
                      duration: 1,
                      delay: 0.5 + index * 0.1
                    }} className="h-full rounded-full bg-gradient-to-r from-pink-500 to-purple-500" data-id="element-922" />
                      </div>
                      <span className="text-sm font-medium text-slate-700 font-mono min-w-[40px]" data-id="element-923">
                        {model.score}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 pl-4" data-id="element-924">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200" data-id="element-925">
                      {model.status}
                    </span>
                  </td>
                </motion.tr>)}
            </tbody>
          </table>
        </div>

        {/* Footer Note */}
        <div className="mt-4 pt-4 border-t border-slate-200" data-id="element-926">
          <p className="text-xs text-slate-500" data-id="element-927">
            Metrics calculated over the last 30 days across {activeRegion}{' '}
            region. Lower MAE and RMSE indicate better accuracy. Bias shows
            systematic over/under-prediction.
          </p>
        </div>
      </motion.div>
    </div>;
}